import { Bell } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { OptionType } from '@liveauctioneers/hammer-ui-core/select';

import SearchFilter from '@/components/Pagination/hammer-pagination/SearchFilter';
import SortPageSizePair from '@/components/Pagination/hammer-pagination/SortPageSizePair';
import { ManageSavedSearchSortOrders } from '@/enums';
import { useAppSelector } from '@/redux/hooks';
import { getTotalSavedSearches, getTotalSavedSearchItems } from '@/redux/modules/savedSearch';
import { PaginationFilter } from '@/types/PaginationFilter';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getNotificationsUrl } from '@/utils/urls';

type Props = {
    filters: PaginationFilter;
    onFilterChange: (filters: PaginationFilter) => void;
};

export const ManageSavedSearchFilter = ({ filters, onFilterChange }: Props) => {
    const manageUrl = getNotificationsUrl('searches');
    const formatMessage = useAppFormatMessage();
    const itemTab = filters.tab === 'items';
    const totalSavedSearchItems = useAppSelector(getTotalSavedSearchItems);
    const totalSavedSearchesCount = useAppSelector(getTotalSavedSearches);

    const OPTIONS: OptionType[] = [
        {
            label: String(formatMessage(['search_alerts', 'sort', 'addedNewest'])),
            value: ManageSavedSearchSortOrders.DATE_ADDED_NEWEST,
        },
        {
            label: String(formatMessage(['search_alerts', 'sort', 'resultCount'])),
            value: ManageSavedSearchSortOrders.RESULT_COUNT,
        },
        {
            label: String(formatMessage(['search_alerts', 'sort', 'addedOldest'])),
            value: ManageSavedSearchSortOrders.DATE_ADDED_OLDEST,
        },
        {
            label: String(formatMessage(['search_alerts', 'sort', 'searchTerm'])),
            value: ManageSavedSearchSortOrders.SEARCH_TERM,
        },
    ];

    return (
        <div className="flex flex-row items-center justify-between pt-lg xsMax:flex-col xsMax:items-start">
            <div className="flex gap-sm xsMax:w-full xsMax:flex-col-reverse">
                <SearchFilter
                    className="w-[240px] xsMax:w-full [&_label]:hidden"
                    onChange={onFilterChange}
                    paginationFilter={filters}
                    searchPlaceholder={String(
                        formatMessage(['search_alerts', itemTab ? 'resultsPlaceholder' : 'filterBySearchTerms'], {
                            values: {
                                count: itemTab ? totalSavedSearchItems : totalSavedSearchesCount,
                            },
                        })
                    )}
                    shouldModifyQueryParams
                    shouldResetPageOnChange
                />
                <SortPageSizePair
                    className="w-full"
                    hideCount
                    isInsideLabel
                    onChange={onFilterChange}
                    paginationFilter={filters}
                    shouldModifyQueryParams
                    sortLabel={formatMessage(['search_alerts', 'sortLabel'])}
                    sortOptions={OPTIONS}
                />
            </div>
            <SubtleMonochromeLink
                className="xsMax:hidden"
                data-testid="manageNotifications"
                href={manageUrl}
                id="manageNotifications"
            >
                <InlineButton icon={Bell}>{formatMessage(['manageNotifications'])}</InlineButton>
            </SubtleMonochromeLink>
        </div>
    );
};
